(() => {
  const VERSION = '2026.08.24.1';

  if (window.BHProductReviews?.version === VERSION) return;

  const translations = new Map([
    ['Customer Reviews', 'Opiniones de nuestros clientes'],
    ['Write a review', 'Escribir una opinión'],
    ['Write A Review', 'Escribir una opinión'],
    ['Most Recent', 'Más recientes'],
    ['Highest Rating', 'Mejor puntuación'],
    ['Lowest Rating', 'Menor puntuación'],
    ['Most Helpful', 'Más útiles'],
    ['With Pictures', 'Con fotos'],
    ['Submit Review', 'Publicar opinión'],
    ['Cancel Review', 'Cancelar'],
    ['Review', 'Opinión'],
    ['Reviews', 'Opiniones'],
    ['Verified Buyer', 'Compra verificada'],
    ['Verified Purchase', 'Compra verificada'],
    ['Sort by', 'Ordenar por'],
    ['Pictures', 'Fotos'],
    ['See more', 'Ver más'],
    ['See less', 'Ver menos'],
    ['Was this review helpful?', '¿Te resultó útil esta opinión?'],
    ['Helpful', 'Útil'],
    ['Yes', 'Sí'],
    ['No', 'No'],
    ['No reviews yet', 'Aún no hay opiniones'],
    ['Be the first to write a review', 'Sé la primera persona en compartir su experiencia']
  ]);

  const normalize = (value) => value.replace(/\s+/g, ' ').trim();

  const translateValue = (value) => {
    const clean = normalize(value);
    if (!clean) return null;
    if (translations.has(clean)) return translations.get(clean);

    let match = clean.match(/^Based on (\d+) reviews?$/i);
    if (match) {
      const count = Number(match[1]);
      return count === 1 ? 'Basado en 1 opinión' : `Basado en ${count} opiniones`;
    }

    match = clean.match(/^(\d+) reviews?$/i);
    if (match) {
      const count = Number(match[1]);
      return count === 1 ? '1 opinión' : `${count} opiniones`;
    }

    return null;
  };

  const translateTextNodes = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        const parent = node.parentElement;
        if (!parent || parent.closest('script, style, textarea')) return NodeFilter.FILTER_REJECT;
        return translateValue(node.nodeValue || '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
      }
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      const translated = translateValue(node.nodeValue || '');
      if (translated) node.nodeValue = translated;
    });
  };

  const translateAttributes = (root) => {
    root.querySelectorAll('[aria-label], [placeholder], [title]').forEach((element) => {
      ['aria-label', 'placeholder', 'title'].forEach((attribute) => {
        if (!element.hasAttribute(attribute)) return;
        const translated = translateValue(element.getAttribute(attribute) || '');
        if (translated) element.setAttribute(attribute, translated);
      });
    });
  };

  const enhanceAccessibility = (root) => {
    root.querySelectorAll('.jdgm-rev').forEach((review) => {
      review.setAttribute('role', 'article');

      const hasPhotos = Boolean(review.querySelector('.jdgm-rev__pics, .jdgm-rev__pic-link, .jdgm-rev__video'));
      const isVerified = Boolean(
        review.querySelector('.jdgm-rev__buyer-badge, [data-verified-buyer], [class*="verified"]') ||
        /verified buyer|verified purchase|compra verificada/i.test(review.textContent || '')
      );

      review.classList.toggle('has-review-media', hasPhotos);
      review.classList.toggle('has-verified-review', isVerified);
    });

    root.querySelectorAll('.jdgm-rev__pic-link').forEach((link) => {
      if (!link.getAttribute('aria-label')) link.setAttribute('aria-label', 'Abrir foto de la opinión');
    });
  };

  const decorateWidget = (section) => {
    const histogram = section.querySelector('.jdgm-histogram');
    if (histogram && !section.querySelector('.bh-reviews__histogram-title')) {
      const title = document.createElement('p');
      title.className = 'bh-reviews__histogram-title';
      title.textContent = 'Distribución de calificaciones';
      histogram.before(title);
      histogram.setAttribute('aria-label', 'Distribución de opiniones por número de estrellas');
    }

    const customButton = section.querySelector('[data-bh-write-review]');
    if (customButton && customButton.dataset.bhBound !== 'true') {
      customButton.dataset.bhBound = 'true';
      customButton.addEventListener('click', () => {
        const nativeTrigger = section.querySelector(
          '.jdgm-write-rev-link, [data-testid="write-review-button"], button[aria-label*="review" i]'
        );

        if (nativeTrigger) nativeTrigger.click();

        window.setTimeout(() => {
          const form = section.querySelector('.jdgm-form-wrapper, form[class*="review"], [data-testid="review-form"]');
          const focusTarget = form?.querySelector('input:not([type="hidden"]), textarea, select, button');

          if (form) form.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }, 180);
      });
    }
  };

  const widgetHasRendered = (section) => {
    const widget = section.querySelector('[data-bh-reviews-widget]');
    if (!widget) return false;

    return Boolean(
      widget.querySelector('.jdgm-rev-widg, .jdgm-rev, .jdgm-form-wrapper, [data-testid="review-widget"]') ||
      normalize(widget.textContent || '').length > 20
    );
  };

  const refresh = (section) => {
    translateTextNodes(section);
    translateAttributes(section);
    enhanceAccessibility(section);
    decorateWidget(section);

    if (widgetHasRendered(section)) section.classList.add('is-ready');
  };

  const initializeSection = (section) => {
    if (section.dataset.bhReviewsInitialized === 'true') return;
    section.dataset.bhReviewsInitialized = 'true';

    let scheduled = false;
    const scheduleRefresh = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        refresh(section);
      });
    };

    const observer = new MutationObserver(scheduleRefresh);
    observer.observe(section, {
      childList: true,
      subtree: true
    });

    scheduleRefresh();

    // Un widget vacío también debe dejar de mostrar el indicador de carga.
    window.setTimeout(() => section.classList.add('is-ready'), 3500);
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-bh-product-reviews]').forEach(initializeSection);
  };

  document.addEventListener('DOMContentLoaded', () => initializeAll());
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));

  if (document.readyState !== 'loading') initializeAll();

  window.BHProductReviews = {
    version: VERSION,
    refresh: () => initializeAll()
  };
})();
